import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Container,
  Typography,
  Box,
  Button,
  Grid,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
} from "@mui/material";
import SyncIcon from "@mui/icons-material/Sync";
import GoogleIcon from "@mui/icons-material/Google";
import CallCard from "../components/CallCard";
import UpcomingCallCard from "../components/UpcomingCallCard";
import ScoreChart from "../components/ScoreChart";
import UpgradeAccountDialog from "../components/UpgradeAccountDialog";
import { getSampleReports } from "../data/sampleReport";
import {
  calendarAuth,
  getCalendarStatus,
  syncCalendar,
  getCalls,
  getMetrics,
  recalculateMetrics as recalculateMetricsCall,
  getUserProfile,
} from "../utils/firebase";

const PAGE_SIZE = 20;

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const [calls, setCalls] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [connected, setConnected] = useState(null);
  const [calendarEmail, setCalendarEmail] = useState(null);
  const [calendarHealthy, setCalendarHealthy] = useState(true);
  const [error, setError] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [metrics, setMetrics] = useState({ avgScore: null, totalCompleted: 0, latestScore: null });
  const [recalcLoading, setRecalcLoading] = useState(false);
  const [canManageMeetings, setCanManageMeetings] = useState(false);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);

  // Sample report (instant aha before the first real call)
  const [sampleVariant, setSampleVariant] = useState("strong");

  const loadMetrics = useCallback(async () => {
    try {
      const result = await getMetrics();
      setMetrics(result.data);
    } catch (err) {
      console.error("Failed to load metrics:", err);
    }
  }, []);

  const loadCalls = useCallback(async () => {
    try {
      const result = await getCalls({ limit: PAGE_SIZE });
      setCalls(result.data.calls || []);
      setHasMore(result.data.hasMore || false);
    } catch (err) {
      console.error("Failed to load calls:", err);
      setError(t("dashboard.failedLoadCalls"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const loadMoreCalls = async () => {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);
    try {
      const lastTimestamp = calls[calls.length - 1]?.timestamp;
      const result = await getCalls({ limit: PAGE_SIZE, before: lastTimestamp });
      const newCalls = result.data.calls || [];
      setCalls((prev) => [...prev, ...newCalls]);
      setHasMore(result.data.hasMore || false);
    } catch (err) {
      console.error("Failed to load more calls:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  const checkConnection = useCallback(async () => {
    try {
      const result = await getCalendarStatus();
      setConnected(result.data.connected);
      setCalendarEmail(result.data.email || null);
      setCalendarHealthy(result.data.healthy !== false);
    } catch {
      setConnected(false);
      setCalendarEmail(null);
      setCalendarHealthy(true);
    }
  }, []);

  const loadUserProfile = useCallback(async () => {
    try {
      const result = await getUserProfile();
      const { teamId, teamRole } = result.data || {};
      setCanManageMeetings(!teamId || teamRole === "admin");
    } catch (err) {
      console.error("Failed to load user profile:", err);
      setCanManageMeetings(false);
    }
  }, []);

  useEffect(() => {
    checkConnection();
    loadCalls();
    loadMetrics();
    loadUserProfile();
  }, [checkConnection, loadCalls, loadMetrics, loadUserProfile]);

  const handleConnect = async () => {
    try {
      const result = await calendarAuth();
      const popup = window.open(result.data.url, "_blank", "width=600,height=700");
      const poll = setInterval(async () => {
        if (!popup || popup.closed) {
          clearInterval(poll);
          await checkConnection();
          await loadCalls();
        }
      }, 1000);
    } catch (err) {
      setError(t("dashboard.failedCalendarConnection"));
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setError(null);
    setSyncResult(null);
    try {
      const result = await syncCalendar();
      if (result.data.error === "not_connected") {
        setConnected(false);
        setError(t("dashboard.connectCalendarFirst"));
        return;
      }
      if (result.data.error === "calendar_auth_revoked") {
        setCalendarHealthy(false);
        setError(t("dashboard.calendarAuthRevoked"));
        return;
      }
      const results = result.data.results || [];
      const scheduled = result.data.scheduled ?? 0;
      const limitReachedCount = results.filter((r) => r.status === "limit_reached").length;
      if (limitReachedCount > 0) {
        setUpgradeDialogOpen(true);
        setSyncResult(
          scheduled === 0
            ? t("dashboard.limitReachedAll", { count: limitReachedCount })
            : t("dashboard.scheduledWithLimit", { scheduled, limit: limitReachedCount })
        );
      } else {
        setSyncResult(t("dashboard.syncedMeetings", { count: scheduled || result.data.synced }));
      }
      await loadCalls();
      await loadMetrics();
    } catch (err) {
      console.error("Sync failed:", err);
      setError(t("dashboard.syncFailed") + ": " + err.message);
    } finally {
      setSyncing(false);
    }
  };

  const now = Date.now();
  const isUpcoming = (c) =>
    (c.status === "scheduled" || c.status === "bot_sent") &&
    c.timestamp &&
    new Date(c.timestamp).getTime() > now;
  const upcomingCalls = calls.filter(isUpcoming);
  const historyCalls = calls.filter((c) => !isUpcoming(c));

  const completedCalls = calls.filter((c) => c.status === "completed" && c.score != null);
  const chartCalls = [...completedCalls].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  const chartData = {
    labels: chartCalls.map((c) => {
      const d = new Date(c.timestamp);
      return d.toLocaleDateString("en-US", { day: "numeric", month: "short" });
    }),
    data: chartCalls.map((c) => c.score),
  };

  const sampleReport = getSampleReports(i18n.language)[sampleVariant];

  const getPerformanceMessage = () => {
    if (loading) return "";
    if (calls.length === 0) {
      return t("dashboard.noCallsDesc");
    }
    const score = metrics.avgScore;
    if (score == null) {
      return t("dashboard.performanceSyncing");
    }
    if (score >= 80) {
      return t("dashboard.performanceOutstanding", { score });
    } else if (score >= 60) {
      return t("dashboard.performanceOnTrack", { score });
    } else {
      return t("dashboard.performanceNeedsFocus", { score });
    }
  };

  const MetricCard = ({ value, label, type, comparisonValue }) => {
    let color = "#111827";
    let statusText = "";
    let badge = null;
    let extraContent = null;

    if (type === "average") {
      if (value != null) {
        if (value >= 80) {
          color = "#10b981";
          statusText = "Outstanding discovery engagement";
          badge = (
            <Box sx={{
              display: "inline-flex",
              alignItems: "center",
              px: "10px",
              py: "4px",
              borderRadius: "12px",
              bgcolor: "rgba(16, 185, 129, 0.08)",
              border: "1px solid rgba(16, 185, 129, 0.15)",
              color: "#059669",
              fontSize: "10px",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              lineHeight: 1,
              fontFamily: '"Inter", sans-serif',
              whiteSpace: "nowrap",
              ml: "12px",
              flexShrink: 0
            }}>
              Top Tier
            </Box>
          );
        } else if (value >= 60) {
          color = "#f59e0b";
          statusText = "On track, focus on next steps";
          badge = (
            <Box sx={{
              display: "inline-flex",
              alignItems: "center",
              px: "10px",
              py: "4px",
              borderRadius: "12px",
              bgcolor: "rgba(245, 158, 11, 0.08)",
              border: "1px solid rgba(245, 158, 11, 0.15)",
              color: "#d97706",
              fontSize: "10px",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              lineHeight: 1,
              fontFamily: '"Inter", sans-serif',
              whiteSpace: "nowrap",
              ml: "12px",
              flexShrink: 0
            }}>
              On Track
            </Box>
          );
        } else {
          color = "#ef4444";
          statusText = "Needs practice on discovery";
          badge = (
            <Box sx={{
              display: "inline-flex",
              alignItems: "center",
              px: "10px",
              py: "4px",
              borderRadius: "12px",
              bgcolor: "rgba(239, 68, 68, 0.08)",
              border: "1px solid rgba(239, 68, 68, 0.15)",
              color: "#dc2626",
              fontSize: "10px",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              lineHeight: 1,
              fontFamily: '"Inter", sans-serif',
              whiteSpace: "nowrap",
              ml: "12px",
              flexShrink: 0
            }}>
              Needs Focus
            </Box>
          );
        }
      } else {
        statusText = "No call data analyzed yet";
      }

      extraContent = (
        <Box sx={{ mt: 1.5 }}>
          <Box sx={{ width: "100%", height: 4, bgcolor: "#f3f4f6", borderRadius: 2, overflow: "hidden", mb: 1 }}>
            <Box sx={{ width: value != null ? `${value}%` : "0%", height: "100%", bgcolor: color, borderRadius: 2, transition: "width 0.8s ease-in-out" }} />
          </Box>
          <Typography sx={{ fontSize: 12, color: "#6b7280" }}>
            {statusText}
          </Typography>
        </Box>
      );
    } else if (type === "total") {
      badge = (
        <Box sx={{
          display: "inline-flex",
          alignItems: "center",
          px: "10px",
          py: "4px",
          borderRadius: "12px",
          bgcolor: "#f3f4f6",
          border: "1px solid #e5e7eb",
          color: "#4b5563",
          fontSize: "10px",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          lineHeight: 1,
          fontFamily: '"Inter", sans-serif',
          whiteSpace: "nowrap",
          ml: "12px",
          flexShrink: 0
        }}>
          Syncing
        </Box>
      );
      extraContent = (
        <Box sx={{ mt: 1.5, pt: 1, borderTop: "1px dashed #e5e7eb" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 6,
                height: 6,
                bgcolor: "#10b981",
                borderRadius: "50%",
                boxShadow: "0 0 0 0 rgba(16, 185, 129, 0.7)",
                animation: "pulse 2s infinite",
                "@keyframes pulse": {
                  "0%": { transform: "scale(0.95)", boxShadow: "0 0 0 0 rgba(16, 185, 129, 0.7)" },
                  "70%": { transform: "scale(1)", boxShadow: "0 0 0 5px rgba(16, 185, 129, 0)" },
                  "100%": { transform: "scale(0.95)", boxShadow: "0 0 0 0 rgba(16, 185, 129, 0)" }
                }
              }}
            />
            <Typography sx={{ fontSize: 12, color: "#6b7280" }}>
              Google Calendar syncing active
            </Typography>
          </Box>
        </Box>
      );
    } else if (type === "latest") {
      const diff = comparisonValue != null && value != null ? value - comparisonValue : null;
      if (diff != null) {
        badge = (
          <Box sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            px: "10px",
            py: "4px",
            borderRadius: "12px",
            bgcolor: diff >= 0 ? "rgba(16, 185, 129, 0.08)" : "rgba(239, 68, 68, 0.08)",
            border: diff >= 0 ? "1px solid rgba(16, 185, 129, 0.15)" : "1px solid rgba(239, 68, 68, 0.15)",
            color: diff >= 0 ? "#059669" : "#dc2626",
            fontSize: "10px",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            lineHeight: 1,
            fontFamily: '"Inter", sans-serif',
            whiteSpace: "nowrap",
            ml: "12px",
            flexShrink: 0
          }}>
            <span>{diff >= 0 ? "↑" : "↓"}</span>
            <span>{Math.round(Math.abs(diff))}% vs avg</span>
          </Box>
        );
      }
      if (value != null) {
        if (value >= 80) color = "#10b981";
        else if (value >= 60) color = "#f59e0b";
        else color = "#ef4444";
      }
      extraContent = (
        <Box sx={{ mt: 1.5 }}>
          <Box sx={{ width: "100%", height: 4, bgcolor: "#f3f4f6", borderRadius: 2, overflow: "hidden", mb: 1 }}>
            <Box sx={{ width: value != null ? `${value}%` : "0%", height: "100%", bgcolor: color, borderRadius: 2, transition: "width 0.8s ease-in-out" }} />
          </Box>
          <Typography sx={{ fontSize: 12, color: "#6b7280" }}>
            {value != null ? `Latest score: ${value}/100` : "No calls analyzed"}
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          p: 3,
          border: "1px solid #e5e7eb",
          borderRadius: 1,
          bgcolor: "#ffffff",
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          position: "relative",
          transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.2s ease",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 12px 24px -10px rgba(0, 0, 0, 0.06), 0 4px 12px -5px rgba(0, 0, 0, 0.03)",
            borderColor: "#111827",
          },
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5} sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: "#9ca3af",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              minWidth: 0,
              flexShrink: 1
            }}
          >
            {label}
          </Typography>
          {badge}
        </Box>
        <Typography
          sx={{
            fontFamily: '"Cal Sans", "Inter", sans-serif',
            fontSize: 38,
            fontWeight: 600,
            letterSpacing: "-0.02em",
            color: "#111827",
            lineHeight: 1.1,
          }}
        >
          {value ?? "—"}
        </Typography>
        {extraContent}
      </Box>
    );
  };

  return (
    <Box sx={{ bgcolor: "#ffffff", minHeight: "100vh" }}>
      <Container maxWidth="lg" sx={{ py: 5, px: { xs: 2, sm: 3 } }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap="wrap"
          gap={2}
          mb={4}
        >
          <Box sx={{ flexGrow: 1, maxWidth: 650 }}>
            <Box display="flex" alignItems="center" gap={1.5} mb={1}>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: '"Cal Sans", "Inter", sans-serif',
                  fontWeight: 600,
                  letterSpacing: "-0.02em",
                  color: "#111827",
                  fontSize: 28,
                }}
              >
                {t("dashboard.title")}
              </Typography>
              {!loading && metrics.avgScore != null && (
                <Chip
                  label={
                    metrics.avgScore >= 80
                      ? "Outstanding"
                      : metrics.avgScore >= 60
                      ? "On Track"
                      : "Needs Focus"
                  }
                  size="small"
                  sx={{
                    bgcolor:
                      metrics.avgScore >= 80
                        ? "#111827"
                        : metrics.avgScore >= 60
                        ? "#f3f4f6"
                        : "transparent",
                    color:
                      metrics.avgScore >= 80
                        ? "#ffffff"
                        : "#111827",
                    border: metrics.avgScore < 60 ? "1px solid #e5e7eb" : "none",
                    fontWeight: 600,
                    fontSize: 11,
                    borderRadius: 1,
                    px: 0.5,
                  }}
                />
              )}
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: "#6b7280",
                fontSize: 14,
                lineHeight: 1.5,
              }}
            >
              {getPerformanceMessage()}
            </Typography>
          </Box>
          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
            {connected === true && calendarHealthy && (
              <Typography sx={{ color: "#059669", fontSize: 13, fontWeight: 500 }}>
                {calendarEmail ? t("dashboard.calendarConnectedWithEmail", { email: calendarEmail }) : t("dashboard.calendarConnected")}
              </Typography>
            )}
            {connected === true && !calendarHealthy && (
              <Typography sx={{ color: "#b45309", fontSize: 13, fontWeight: 500 }}>
                {t("dashboard.calendarReconnectNeededShort")}
              </Typography>
            )}
            {connected === false && (
              <Button
                variant="outlined"
                startIcon={<GoogleIcon />}
                onClick={handleConnect}
                sx={{ textTransform: "none" }}
              >
                {t("dashboard.connectGoogleCalendar")}
              </Button>
            )}
            {connected === true && (
              <Button
                variant="contained"
                startIcon={syncing ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
                onClick={handleSync}
                disabled={syncing}
                sx={{
                  bgcolor: "#111827",
                  textTransform: "none",
                  "&:hover": { bgcolor: "#374151" },
                }}
              >
                {syncing ? t("dashboard.syncing") : t("dashboard.syncCalls")}
              </Button>
            )}
          </Box>
        </Box>

        {connected === true && !calendarHealthy && (
          <Alert
            severity="warning"
            sx={{ mb: 3 }}
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<GoogleIcon />}
                onClick={handleConnect}
                sx={{ textTransform: "none" }}
              >
                {t("dashboard.reconnect")}
              </Button>
            }
          >
            {t("dashboard.calendarReconnectBanner")}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {syncResult && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSyncResult(null)}>
            {syncResult}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={10}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {calls.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <MetricCard value={metrics.avgScore} label={t("dashboard.averageScore")} type="average" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <MetricCard value={metrics.totalCompleted} label={t("dashboard.analyzedCalls")} type="total" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <MetricCard value={metrics.latestScore} label={t("dashboard.latestScore")} type="latest" comparisonValue={metrics.avgScore} />
                  </Grid>
                </Grid>
                {canManageMeetings && (
                  <Button
                    size="small"
                    sx={{ mt: 1, textTransform: "none", color: "#6b7280" }}
                    disabled={recalcLoading}
                    onClick={async () => {
                      setRecalcLoading(true);
                      try {
                        await recalculateMetricsCall();
                        await loadMetrics();
                      } finally {
                        setRecalcLoading(false);
                      }
                    }}
                  >
                    {recalcLoading ? t("dashboard.recalculating") : t("dashboard.recalculateMetrics")}
                  </Button>
                )}
              </Box>
            )}

            {chartData.data.length > 0 && (
              <ScoreChart
                labels={chartData.labels}
                data={chartData.data}
                title={t("dashboard.scoreTrend")}
              />
            )}

            {/* Upcoming calls RepRadar will join (locked and loaded) */}
            {upcomingCalls.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: '"Cal Sans", "Inter", sans-serif',
                    fontWeight: 600,
                    letterSpacing: "0.02em",
                    color: "#111827",
                    mb: 2,
                    fontSize: 18,
                  }}
                >
                  {t("upcoming.heading")}
                </Typography>
                {upcomingCalls.map((call) => (
                  <UpcomingCallCard
                    key={call.id}
                    call={call}
                    onRefresh={() => {
                      loadCalls();
                    }}
                  />
                ))}
              </Box>
            )}

            {/* Sample report — instant aha when there's nothing to show yet */}
            {calls.length === 0 && (
              <Box sx={{ mb: 4 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={2}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: '"Cal Sans", "Inter", sans-serif',
                      fontWeight: 600,
                      color: "#111827",
                      fontSize: 18,
                    }}
                  >
                    {t("dashboard.sampleHeading")}
                  </Typography>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={sampleVariant}
                    onChange={(_e, v) => v && setSampleVariant(v)}
                  >
                    <ToggleButton value="strong" sx={{ textTransform: "none" }}>
                      {t("dashboard.sampleStrong")}
                    </ToggleButton>
                    <ToggleButton value="rough" sx={{ textTransform: "none" }}>
                      {t("dashboard.sampleRough")}
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <Typography sx={{ color: "#6b7280", fontSize: 14, mb: 2 }}>
                  {t("dashboard.sampleSubtitle")}
                </Typography>
                <CallCard call={sampleReport} isSample sampleVariant={sampleVariant} canManageMeetings={false} />
                {connected !== true && (
                  <Button
                    variant="contained"
                    startIcon={<GoogleIcon />}
                    onClick={handleConnect}
                    sx={{ mt: 1, bgcolor: "#111827", textTransform: "none", "&:hover": { bgcolor: "#374151" } }}
                  >
                    {t("dashboard.connectGoogleCalendar")}
                  </Button>
                )}
              </Box>
            )}

            {historyCalls.length > 0 && (
              <>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: '"Cal Sans", "Inter", sans-serif',
                    fontWeight: 600,
                    letterSpacing: "0.02em",
                    color: "#111827",
                    mb: 2,
                    fontSize: 18,
                  }}
                >
                  {t("dashboard.callHistory")}
                </Typography>
                {historyCalls.map((call) => (
                  <CallCard
                    key={call.id}
                    call={call}
                    onRefresh={() => {
                      loadCalls();
                      loadMetrics();
                    }}
                    canManageMeetings={canManageMeetings}
                    onLimitReached={() => setUpgradeDialogOpen(true)}
                  />
                ))}
                {hasMore && (
                  <Box display="flex" justifyContent="center" mt={3}>
                    <Button
                      variant="outlined"
                      onClick={loadMoreCalls}
                      disabled={loadingMore}
                      sx={{ textTransform: "none", minWidth: 160 }}
                    >
                      {loadingMore ? <CircularProgress size={20} /> : t("dashboard.loadMore")}
                    </Button>
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </Container>
      <UpgradeAccountDialog open={upgradeDialogOpen} onClose={() => setUpgradeDialogOpen(false)} />
    </Box>
  );
}
